import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { registry } from '../../core/Registry.js';

const CountdownWrapper = styled.div`
  display: flex;
  justify-content: ${props => props.align || 'center'};
  gap: 15px;
  flex-wrap: wrap;
`;

const TimeBox = styled.div`
  background: var(--glass-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm, 12px);
  padding: 15px 20px;
  min-width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  backdrop-filter: blur(var(--glass-blur, 10px));
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
`;

const NumberLabel = styled.div`
  font-size: ${props => props.size || '32px'};
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
  margin-bottom: 5px;
`;

const TextLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-muted);
  letter-spacing: 1px;
`;

const CountdownWidget = ({ node, isLiveMode }) => {
  const targetDateStr = node.content?.targetDate || '';
  const align = node.style?.desktop?.align || 'center';
  const numSize = node.style?.desktop?.numberSize || '32px';

  const [timeLeft, setTimeLeft] = useState({
    days: 0, hours: 0, minutes: 0, seconds: 0
  });

  useEffect(() => {
    if(!targetDateStr) return;
    
    const target = new Date(targetDateStr).getTime();
    
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const distance = target - now;

      if (distance < 0) {
        clearInterval(interval);
        setTimeLeft({days: 0, hours: 0, minutes: 0, seconds: 0});
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDateStr]);

  if (!targetDateStr) {
    return (
      <div style={{padding: 20, textAlign: 'center', background: 'var(--glass-1)', border: '1px dashed var(--accent)', color: 'var(--text-muted)'}}>
        Countdown (Bitte Zieldatum eintragen: YYYY-MM-DD HH:MM)
      </div>
    );
  }

  return (
    <CountdownWrapper align={align}>
      <TimeBox>
        <NumberLabel size={numSize}>{timeLeft.days}</NumberLabel>
        <TextLabel>Tage</TextLabel>
      </TimeBox>
      <TimeBox>
        <NumberLabel size={numSize}>{timeLeft.hours}</NumberLabel>
        <TextLabel>Stun</TextLabel>
      </TimeBox>
      <TimeBox>
        <NumberLabel size={numSize}>{timeLeft.minutes}</NumberLabel>
        <TextLabel>Min</TextLabel>
      </TimeBox>
      <TimeBox>
        <NumberLabel size={numSize}>{timeLeft.seconds}</NumberLabel>
        <TextLabel>Sek</TextLabel>
      </TimeBox>
    </CountdownWrapper>
  );
};

registry.register('countdown_timer', {
  label: 'Live Countdown',
  icon: '⏳',
  category: 'advanced',
  defaultContent: {
    targetDate: '2027-12-31 23:59'
  },
  defaultStyle: {
    desktop: {
      align: 'center',
      numberSize: '32px'
    }
  },
  render: CountdownWidget,
  inspectorControls: [
    {
      tab: 'content',
      groups: [
        {
          title: 'Countdown Ziel',
          fields: [
            { name: 'content.targetDate', label: 'Zieldatum (YYYY-MM-DD HH:MM)', type: 'text' }
          ]
        }
      ]
    },
    {
      tab: 'style',
      groups: [
        {
          title: 'Layout & Typografie',
          fields: [
            { name: 'style.desktop.align', label: 'Ausrichtung', type: 'select', options: [{label:'Links', value:'flex-start'}, {label:'Zentriert', value:'center'}, {label:'Rechts', value:'flex-end'}] },
            { name: 'style.desktop.numberSize', label: 'Zahlengröße', type: 'text' }
          ]
        }
      ]
    }
  ]
});
